import React from 'react';
import { Link } from 'react-router-dom';
import { Menu, X } from 'lucide-react';

/**
 * Linkerkolom in BeLagom-huisstijl (okt 2026): grote navy titel, zachte gouden
 * achtergrond voor het actieve item en navy iconen. Geen contactgegevens: contact
 * met BeLagom loopt via de chat.
 * Eén component voor alle cockpits, zodat de menu's overal gelijk ogen.
 */

type Icoon = React.ComponentType<{ className?: string }>;

export interface ZijMenuItem {
  key: string;
  label: string;
  icon?: Icoon;
  /** Link-item; zonder href wordt het een knop met onClick */
  href?: string;
  onClick?: () => void;
  actief?: boolean;
  omschrijving?: string;
  /** Teller rechts in het item (bv. ongelezen) */
  aantal?: number;
}

export interface ZijMenuGroep {
  label?: string;
  items: ZijMenuItem[];
}

export const zijMenuItemKlasse = (actief: boolean) =>
  `w-full flex items-center gap-3 px-4 py-3 text-sm rounded-lg transition-colors text-left ${
    actief ? 'bg-gold-100 text-navy-900 font-semibold' : 'text-navy-800 font-medium hover:bg-navy-50'
  }`;

const ItemInhoud: React.FC<{ item: ZijMenuItem }> = ({ item }) => {
  const Icon = item.icon;
  return (
    <>
      {Icon && <Icon className="w-5 h-5 flex-shrink-0 text-navy-900" />}
      <span className="flex-1 min-w-0">
        {item.label}
        {item.omschrijving && <span className="block text-xs font-normal text-navy-400 mt-0.5">{item.omschrijving}</span>}
      </span>
      {(item.aantal ?? 0) > 0 && (
        <span className="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full bg-gold-400 text-navy-900 text-xs font-bold">
          {(item.aantal ?? 0) > 99 ? '99+' : item.aantal}
        </span>
      )}
    </>
  );
};

interface ZijMenuInhoudProps {
  titel?: string;
  ondertitel?: string;
  /** Vrij blok boven de titel (bv. profielfoto of organisatielogo) */
  kop?: React.ReactNode;
  groepen: ZijMenuGroep[];
  /** Alleen in de mobiele lade: sluitknop en sluiten na een klik */
  onSluit?: () => void;
}

export const ZijMenuInhoud: React.FC<ZijMenuInhoudProps> = ({ titel, ondertitel, kop, groepen, onSluit }) => (
  <div className="p-4 flex flex-col min-h-full">
    <div className="flex items-start justify-between gap-2">
      <div className="min-w-0">
        {kop}
        {titel && <h1 className="text-2xl font-bold text-navy-900 mb-1">{titel}</h1>}
        {ondertitel && <p className="text-sm text-navy-500 mb-5 leading-snug">{ondertitel}</p>}
      </div>
      {onSluit && (
        <button type="button" onClick={onSluit} className="md:hidden p-2 rounded-lg text-navy-500 hover:bg-navy-50" aria-label="Sluiten">
          <X className="w-5 h-5" />
        </button>
      )}
    </div>
    <nav className="space-y-4">
      {groepen.map((groep, gi) => (
        <div key={groep.label ?? gi}>
          {groep.label && <p className="px-4 mb-1 text-xs font-semibold text-navy-400 uppercase tracking-wider">{groep.label}</p>}
          <div className="space-y-1">
            {groep.items.map((item) =>
              item.href ? (
                <Link
                  key={item.key}
                  to={item.href}
                  onClick={() => {
                    item.onClick?.();
                    onSluit?.();
                  }}
                  className={zijMenuItemKlasse(!!item.actief)}
                  aria-current={item.actief ? 'page' : undefined}
                >
                  <ItemInhoud item={item} />
                </Link>
              ) : (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => {
                    item.onClick?.();
                    onSluit?.();
                  }}
                  className={zijMenuItemKlasse(!!item.actief)}
                  aria-current={item.actief ? 'page' : undefined}
                >
                  <ItemInhoud item={item} />
                </button>
              ),
            )}
          </div>
        </div>
      ))}
    </nav>
  </div>
);

interface ZijMenuProps extends Omit<ZijMenuInhoudProps, 'onSluit'> {
  mobielOpen?: boolean;
  onMobielSluit?: () => void;
  /** Tailwind-breedte van de desktopkolom */
  breedte?: string;
}

/** Desktopkolom + mobiele lade. */
export const ZijMenu: React.FC<ZijMenuProps> = ({ mobielOpen, onMobielSluit, breedte = 'w-72', ...inhoud }) => (
  <>
    <aside className={`hidden md:block ${breedte} bg-white border-r border-gray-200 flex-shrink-0 overflow-y-auto`}>
      <ZijMenuInhoud {...inhoud} />
    </aside>
    {mobielOpen && (
      <div className="fixed inset-0 z-40 md:hidden">
        <div className="fixed inset-0 bg-black/50" onClick={onMobielSluit} />
        <div className="fixed inset-y-0 left-0 w-72 bg-white shadow-xl overflow-y-auto z-50">
          <ZijMenuInhoud {...inhoud} onSluit={onMobielSluit} />
        </div>
      </div>
    )}
  </>
);

/** Knop die op mobiel de lade opent. */
export const ZijMenuMobielKnop: React.FC<{ onClick: () => void; label?: string }> = ({ onClick, label = 'Menu' }) => (
  <div className="md:hidden px-4 py-2 bg-white border-b border-gray-200">
    <button type="button" onClick={onClick} className="flex items-center gap-2 text-sm font-medium text-navy-800 hover:text-navy-900">
      <Menu className="w-5 h-5" />
      {label}
    </button>
  </div>
);

/** Paginatitel bovenaan de inhoudskolom, met optionele acties/tellers rechts. */
export const PaginaKop: React.FC<{
  titel: React.ReactNode;
  ondertitel?: React.ReactNode;
  label?: string;
  acties?: React.ReactNode;
  className?: string;
}> = ({ titel, ondertitel, label, acties, className = '' }) => (
  <div className={`flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 ${className}`}>
    <div className="min-w-0">
      {label && <p className="text-xs font-semibold uppercase tracking-widest text-gold-700 mb-1">{label}</p>}
      <h1 className="text-2xl sm:text-3xl font-bold text-navy-900">{titel}</h1>
      {ondertitel && <p className="mt-1 text-sm sm:text-base text-navy-500">{ondertitel}</p>}
    </div>
    {acties && <div className="flex flex-wrap items-center gap-2 flex-shrink-0">{acties}</div>}
  </div>
);

/** Teller-chip in de stijl van de agenda (bolletje met aantal + label). */
export const TellerChip: React.FC<{
  aantal: React.ReactNode;
  label: string;
  toon?: 'goud' | 'swirl' | 'navy';
  nadruk?: boolean;
  href?: string;
  onClick?: () => void;
}> = ({ aantal, label, toon = 'goud', nadruk, href, onClick }) => {
  const bol = toon === 'goud' ? 'bg-gold-400 text-navy-900' : toon === 'swirl' ? 'bg-swirl-400 text-navy-900' : 'bg-navy-900 text-white';
  const klasse = `inline-flex items-center gap-2 pl-1.5 pr-3 py-1 rounded-full border text-sm transition-colors ${
    nadruk ? 'bg-gold-50 border-gold-200 text-navy-900 font-medium' : 'bg-white border-gray-200 text-navy-700 hover:bg-navy-50'
  }`;
  const inhoud = (
    <>
      <span className={`inline-flex items-center justify-center min-w-[1.5rem] h-6 px-1.5 rounded-full text-xs font-bold ${bol}`}>{aantal}</span>
      <span className="whitespace-nowrap">{label}</span>
    </>
  );
  if (href) return <Link to={href} className={klasse}>{inhoud}</Link>;
  if (onClick) return <button type="button" onClick={onClick} className={klasse}>{inhoud}</button>;
  return <span className={klasse}>{inhoud}</span>;
};
